import { type Carer, UserRole } from '@lmc/ts-common'
import { Button } from '@lmc/ui-kit'
import { Heading } from '@lmc/ui-kit/src/general'
import { CalendarNav, CalendarNext, CalendarPrev } from '@mobiscroll/react'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import type { CountChangesResponse } from 'screens/Rostering/hooks/useCountChanges'
import type { RotaBuilderFiltersType } from 'screens/Rostering/types'
import { getPublishCount, getPublishType } from 'screens/Rostering/utils'
import {
  LONG_DATE_FORMAT,
  YEAR_FIRST_DATE_FORMAT
} from 'screens/Rostering/utils.datetime'
import styled from 'styled-components'
import { checkHasFilters } from 'utils'
import PublishModal from '../modals/PublishModal'
import RotaBuilderDropdown from '../RotaBuilderDropdown'

dayjs.extend(isoWeek)

type EventCalendarHeaderProps = {
  selectedDate: Date
  onCalendarChange: (event: any) => void
  handleZoom: () => void
  isZoomed: boolean
  isDailyView: boolean
  isStarted?: boolean
  countChangesData: CountChangesResponse
  publishedRota?: boolean
  filterParams?: RotaBuilderFiltersType
  printView?: any
  showAllEvents?: boolean
  onToggleShowAllEvents?: () => void
}

const EventCalendarHeader = ({
  selectedDate,
  onCalendarChange,
  handleZoom,
  isZoomed,
  isDailyView,
  countChangesData,
  publishedRota,
  filterParams,
  showAllEvents,
  onToggleShowAllEvents
}: // printView,
EventCalendarHeaderProps) => {
  const { t } = useTranslation()

  const userRole = useSelector(
    (state: { global: { user: Carer } }) => state.global.user?.role
  )
  const isAuditView = userRole === UserRole.auditView

  const flags = useFeatureFlags({
    flagNames: ['rostering_dev_features']
  })?.data

  const flagsRosteringDevFeaturesEnabled =
    flags?.rostering_dev_features?.enabled

  const date = dayjs(selectedDate)

  const fromDate = isDailyView
    ? date.format(YEAR_FIRST_DATE_FORMAT)
    : date.startOf('isoWeek').format(YEAR_FIRST_DATE_FORMAT)

  const toDate = isDailyView
    ? date.format(YEAR_FIRST_DATE_FORMAT)
    : date.endOf('isoWeek').format(YEAR_FIRST_DATE_FORMAT)

  const hasPublishFilters = checkHasFilters(filterParams)

  const {
    draftEvents,
    draftWithChanges,
    changesToPublishedEvents,
    filteredDrafts,
    filteredDraftChanges,
    filteredPublishedChanges
  } = countChangesData

  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false)

  const openPublishModal = useCallback(() => setIsPublishModalOpen(true), [])

  const closePublishModal = useCallback(() => setIsPublishModalOpen(false), [])

  const dateFormatInfo = 'MMMM YYYY'
  const dateText = dayjs(selectedDate).format(dateFormatInfo)

  const hasDrafts = hasPublishFilters
    ? filteredDrafts + filteredDraftChanges > 0
    : draftEvents + draftWithChanges > 0
  const hasChanges = hasPublishFilters
    ? filteredPublishedChanges > 0
    : changesToPublishedEvents > 0

  const type = getPublishType(hasDrafts, hasChanges, false, false)

  const publishCount = getPublishCount(
    type,
    hasPublishFilters,
    countChangesData
  )

  const showPublishBtn = !!publishCount
  const isPublishBtnDisabled = !showPublishBtn

  let publishText = t('PUBLISH')
  let publishCountText = ''

  switch (type) {
    case 'publish_draft_events':
      publishText = t('PUBLISH_DRAFT_EVENTS')
      break
    case 'publish_changes':
      publishText = t('PUBLISH_CHANGES')
      break
    case 'publish_all':
      publishText = t('PUBLISH')
      break
    default:
      publishText = t('PUBLISH')
      break
  }

  publishCountText = `(${publishCount})`

  // leaving this here temporarily
  // const scrollToBottom = () => {
  //   window.scrollTo({
  //     top: document.documentElement.scrollHeight,
  //     behavior: 'smooth',
  //   })
  // }

  return (
    <CalendarHeader>
      <LeftContainer>
        <DateText level={4}>{dateText}</DateText>
      </LeftContainer>
      <CenterContainer>
        <CalendarSwitcher>
          <CalendarPrev />
          <CalendarNavContainer>
            <CalendarNav className="calendar-nav" />
            <DatePickerContainer>
              {isDailyView ? (
                <PickerDateText level={4} weight="bold">
                  {date.format(LONG_DATE_FORMAT)}
                </PickerDateText>
              ) : (
                <PickerDateText level={4} weight="bold">
                  {date.startOf('isoWeek').format(LONG_DATE_FORMAT)} -{' '}
                  {date.endOf('isoWeek').format(LONG_DATE_FORMAT)}
                </PickerDateText>
              )}
            </DatePickerContainer>
          </CalendarNavContainer>
          <CalendarNext />
        </CalendarSwitcher>
      </CenterContainer>
      <RightContainerSwitcher>
        {!flagsRosteringDevFeaturesEnabled && (
          <>
            {/* <Button onClick={printView}>Print</Button> */}
            {/* <Button onClick={scrollToBottom}>Full Screen</Button> */}
          </>
        )}

        <RotaBuilderDropdown
          currentDate={date}
          isDailyView={isDailyView}
          onViewChange={onCalendarChange}
          isZoomed={isZoomed}
          handleZoom={handleZoom}
          publishedRota={publishedRota}
          showAllEvents={showAllEvents}
          onToggleShowAllEvents={onToggleShowAllEvents}
        />

        {!publishedRota && !isAuditView && (
          <PublishButton
            data-testid="rota-builder-publish-btn"
            variant="primary"
            disabled={isPublishBtnDisabled}
            onClick={openPublishModal}
          >
            {publishText} {publishCountText}
          </PublishButton>
        )}
        {isPublishModalOpen && (
          <PublishModal
            open={isPublishModalOpen}
            onClose={closePublishModal}
            selectedDate={selectedDate}
            isDailyView={isDailyView}
            fromDate={fromDate}
            toDate={toDate}
            filterParams={filterParams}
          />
        )}
      </RightContainerSwitcher>
    </CalendarHeader>
  )
}

export default EventCalendarHeader

const DateText = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3};
`

const PickerDateText = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3};
  z-index: 10;
  pointer-events: none;
`

const CalendarNavContainer = styled.div`
  display: grid;
  &:hover div {
    cursor: pointer;
    color: ${(props) => props.theme.color.status.primary};
  }
`

const DatePickerContainer = styled.div`
  grid-area: 1 / 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
`

const CalendarSwitcher = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

const CalendarHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  height: 34px;
`

const LeftContainer = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  flex: 1;
`

const RightContainerSwitcher = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  flex: 1;
  justify-content: flex-end;
`

const CenterContainer = styled.div`
  flex: 1;
`

const PublishButton = styled(Button)`
  min-width: 140px;
  text-align: center;
  align-items: center;
  justify-content: center;
`
